import apiClient from "src/utils/apiClient";
import {
  ProductClassification,
  AIResearchResponse,
  AiExternalResearchRequest,
  ReclassifyWithAttributesRequest,
  GetItemInformationRequest,
  GetItemInformationResponse,
  ItemInformation,
  ClassifyWasteProfileRequest,
  ClassifyWasteProfileResponse,
  ClassificationFeedbackRequest,
  ClassificationFeedbackResponse,
} from "src/types/classificationTypes";

export const getItemInformation = async (
  itemId: string,
  siteName: string,
  locale = "en-US",
): Promise<ProductClassification> => {
  const request: GetItemInformationRequest = {
    id: itemId,
    siteName,
    locale,
    imageDimensions: [{ label: "medium", height: 400, width: 400 }],
  };

  const response = await apiClient.post<GetItemInformationResponse>(
    "/classification/item/information",
    request,
  );

  const items = response.data.itemsInformationList;
  if (!items || items.length === 0) {
    throw new Error(`No item information found for identifier: ${itemId}`);
  }

  return mapToProductClassification(items[0]);
};

// Api for classifying product from multiple captured images.
export const classifyProductFromImages = async (
  images: File[],
  siteCode: string,
  classificationId?: string,
): Promise<AIResearchResponse> => {
  const formData = new FormData();
  images.forEach((file) => formData.append("images", file));
  formData.append("site_code", siteCode);
  if (classificationId) {
    formData.append("classification_id", classificationId);
  }

  const response = await apiClient.post<AIResearchResponse>(
    "/classification/ai-research",
    formData,
  );
  return response.data;
};

// Helper function to map the API response to the frontend type.

function mapToProductClassification(item: ItemInformation): ProductClassification {
  const wasteProfiles = (item.complianceAttribute?.wasteProfile ?? []).map((profile) => ({
    id: profile.value,
  }));

  const images: { url: string }[] = [];
  for (const variantSet of item.productImages ?? []) {
    const url = variantSet[0]?.url;
    if (url) images.push({ url });
  }

  const sdsList = (item.safetyDataSheetAttribute?.englishSdsList ?? []).map((sds) => ({
    url: sds.url,
    language: sds.languages?.[0],
  }));

  const productAttribute = item.productAttribute;
  const productAttributes = productAttribute
    ? {
        weight: productAttribute.weight,
        dimensions: productAttribute.dimensions,
        flash_point: productAttribute.flashPoint,
        light_type: productAttribute.lightType,
        hazmat_state: productAttribute.hazmatStateOfMatter,
        unid: productAttribute.unid?.map((u) => u.value),
        hazmat_statement: productAttribute.hazmatStatement?.map((h) => ({ value: h.value })),
        ph_value: productAttribute.phValue,
        product_group: productAttribute.productGroup,
      }
    : undefined;

  return {
    asin: item.asin,
    name: productAttribute?.productName,
    marketplace_id: item.marketplaceId,
    images,
    waste_profiles: wasteProfiles,
    waste_profile_source: item.complianceAttribute?.wasteProfileSource,
    waste_category: item.complianceAttribute?.wasteCategory,
    waste_category_type: item.complianceAttribute?.wasteCategoryType,
    is_hazardous: item.complianceAttribute?.isHazardous,
    transportation_classification: item.complianceAttribute?.transportationClassification,
    hazmat_type: item.complianceAttribute?.hazmatType,
    hazmat_exceptions: item.complianceAttribute?.hazmatExceptions?.map((e) => e.value),
    profile_rationale: item.complianceAttribute?.profileRationale,
    eligible_for_donation: item.eligibleForDonation,
    donation_eligibility_information: item.donationEligibilityInformation,
    donation_categories: item.complianceAttribute?.donationCategories,
    sds_list: sdsList.length > 0 ? sdsList : undefined,
    product_attributes: productAttributes,
    processing_attribute: item.processingAttribute,
  };
}
// API endpoint to retrieve waste profile details.
export const classifyWasteProfile = async (
  request: ClassifyWasteProfileRequest,
): Promise<ClassifyWasteProfileResponse> => {
  const response = await apiClient.post<ClassifyWasteProfileResponse>(
    "/classification/item/waste-profile",
    request,
  );
  return response.data;
};

// Manually triggered AI external research: researches an SDS for the ASIN and
// returns an AI_EXTERNAL_RESEARCH waste profile.
export const initiateAiExternalResearch = async (
  request: AiExternalResearchRequest,
): Promise<ClassifyWasteProfileResponse> => {
  const response = await apiClient.post<ClassifyWasteProfileResponse>(
    "/classification/item/ai-research",
    request,
  );
  return response.data;
};

export const submitClassificationFeedback = async (
  request: ClassificationFeedbackRequest,
): Promise<ClassificationFeedbackResponse> => {
  const response = await apiClient.post<ClassificationFeedbackResponse>(
    "/classification/feedback",
    request,
  );
  return response.data;
};

export const reclassifyWithAttributes = async (
  request: ReclassifyWithAttributesRequest,
): Promise<ClassifyWasteProfileResponse> => {
  const response = await apiClient.post<ClassifyWasteProfileResponse>(
    "/classification/item/reclassify",
    request,
  );
  return response.data;
};
